import { Eye, EyeSlash } from '@phosphor-icons/react'
import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { client } from '../api/client'
import { LanguageSwitcher } from '../components/LanguageSwitcher'

interface SetNewPasswordForm {
  password: string
  passwordConfirm: string
}

export function SetNewPasswordPage() {
  const { t } = useTranslation('auth')
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [tokenValid, setTokenValid] = useState<boolean | null>(null)
  const [tokenError, setTokenError] = useState('')
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState('')
  const [passwordError, setPasswordError] = useState('')
  const [showPassword, setShowPassword] = useState(false)

  const { register, handleSubmit, formState: { errors }, getValues } = useForm<SetNewPasswordForm>()

  // Validate the token when component mounts
  useEffect(() => {
    if (token) {
      validateToken()
    } else {
      // No token provided, redirect to reset password page
      navigate('/reset-password')
    }
  }, [token])

  const validateToken = async () => {
    try {
      const response = await client.post('/auth/verify-reset-token/', { token });
      setTokenValid(response.data.success);
      if (!response.data.success) {
        setTokenError(response.data.message);
      }
    } catch (err: any) {
      setTokenValid(false);
      // Check if the error message indicates an expired or used token
      const errorMessage = err.response?.data?.message || '';
      if (errorMessage.includes('expired') || errorMessage.includes('already been used')) {
        setTokenError(t('resetPassword.linkExpired'));
      } else {
        setTokenError(t('setNewPassword.invalidToken'));
      }
    }
  }

  const onSubmit = async (data: SetNewPasswordForm) => {
    setIsSubmitting(true)
    setError('')
    setPasswordError('')
    
    try {
      const response = await client.post('/auth/reset-password/', {
        token,
        password: data.password,
        password_confirm: data.passwordConfirm
      })
      
      if (response.data.success) {
        setSuccess(true)
      } else {
        setError(response.data.message || t('setNewPassword.error'))
      }
    } catch (err: any) {
      console.error('Password reset error:', err.response?.data);
      
      // Check for password-specific errors
      if (err.response?.data?.password) {
        // Check for common password error
        if (Array.isArray(err.response.data.password) && 
            err.response.data.password.some((msg: string) => msg.includes('This password is too common'))) {
          setPasswordError(t('register.passwordTooCommon'))
        } else {
          // Set the appropriate error message based on the backend response
          if (Array.isArray(err.response.data.password)) {
            setPasswordError(err.response.data.password[0])
          } else {
            setPasswordError(err.response.data.password)
          }
        }
      } else {
        setError(err.response?.data?.message || t('setNewPassword.error'))
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword)
  }

  // Show loading state while validating token
  if (tokenValid === null) {
    return (
      <>
        {/* Fixed Language Switcher */}
        <div className="fixed top-4 right-4 z-50">
          <LanguageSwitcher />
        </div>

        <div className="auth-container" style={{ minHeight: '100vh' }}>
          <div className="max-w-md w-full">
            {/* Standalone Logo */}
            <div className="flex justify-center mb-8">
              <img src="/images/logo.png" alt="Logo" />
            </div>

            <div className="card-neobrutalism">
              <div className="flex justify-center my-6">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div>
              </div>
              <p className="text-center mb-4">{t('setNewPassword.validating')}</p>
            </div>
          </div>
        </div>
      </>
    )
  }

  // Show error if token is invalid
  if (tokenValid === false) {
    return (
      <>
        {/* Fixed Language Switcher */}
        <div className="fixed top-4 right-4 z-50">
          <LanguageSwitcher />
        </div>

        <div className="auth-container" style={{ minHeight: '100vh' }}>
          <div className="max-w-md w-full">
            {/* Standalone Logo */}
            <div className="flex justify-center mb-8">
              <img src="/images/logo.png" alt="Logo" />
            </div>

            <div className="card-neobrutalism">
              <div className="bg-red-100 border-2 border-black p-4 mb-6">
                <div className="text-sm font-bold text-red-900">{tokenError || t('setNewPassword.invalidToken')}</div>
              </div>
              
              <div className="flex justify-center">
                <Link to="/reset-password" className="btn-neobrutalism">
                  {t('setNewPassword.requestNewLink')}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </>
    )
  }

  // Show success message if password was reset successfully
  if (success) {
    return (
      <>
        {/* Fixed Language Switcher */}
        <div className="fixed top-4 right-4 z-50">
          <LanguageSwitcher />
        </div>

        <div className="auth-container" style={{ minHeight: '100vh' }}>
          <div className="max-w-md w-full">
            {/* Standalone Logo */}
            <div className="flex justify-center mb-8">
              <img src="/images/logo.png" alt="Logo" />
            </div>

            <div className="card-neobrutalism">
              <h2 className="text-xl font-bold text-center mb-4">{t('setNewPassword.successTitle')}</h2>
              <p className="text-center mb-6">
                {t('setNewPassword.successMessage')}
              </p>
              <div className="flex justify-center">
                <Link to="/login" className="btn-neobrutalism">
                  {t('setNewPassword.loginNow')}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </>
    )
  }

  // Main form for setting new password
  return (
    <>
      {/* Fixed Language Switcher */}
      <div className="fixed top-4 right-4 z-50">
        <LanguageSwitcher />
      </div>

      <div className="auth-container" style={{ minHeight: '100vh' }}>
        <div className="max-w-md w-full">
          {/* Standalone Logo */}
          <div className="flex justify-center mb-8">
            <img src="/images/logo.png" alt="Logo" />
          </div>

          <div className="card-neobrutalism mb-8">
            <h2 className="text-xl font-bold text-center mb-6">{t('setNewPassword.title')}</h2>
            
            <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
              {error && (
                <div className="bg-red-100 border-2 border-black p-4">
                  <div className="text-sm font-bold text-red-900">{error}</div>
                </div>
              )}
              
              <div className="space-y-4">
                <div>
                  <label htmlFor="password-input" className="block text-sm font-bold text-neobrutalism-text mb-2">
                    {t('setNewPassword.passwordLabel')}
                  </label>
                  <div className="relative">
                    <input
                      id="password-input"
                      {...register('password', {
                        required: t('setNewPassword.passwordRequired'),
                        minLength: {
                          value: 8,
                          message: t('register.passwordMinLength')
                        },
                        pattern: {
                          // Pattern to check for at least one letter and one number
                          value: /^(?=.*[A-Za-z])(?=.*\d).+$/,
                          message: t('register.passwordComplexity')
                        }
                      })}
                      type={showPassword ? "text" : "password"}
                      className="input-neobrutalism w-full pr-10"
                      placeholder={t('setNewPassword.passwordPlaceholder')}
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      onClick={togglePasswordVisibility}
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                  {errors.password && (
                    <p className="mt-1 text-sm font-bold text-red-600">{errors.password.message}</p>
                  )}
                  {passwordError && (
                    <p className="mt-1 text-sm font-bold text-red-600">{passwordError}</p>
                  )}
                  {/* Password requirements hint */}
                  <div className="mt-2 p-2 bg-gray-50 border border-gray-200 rounded text-xs text-gray-600">
                    <ul className="list-disc pl-4 space-y-1">
                      <li>{t('register.passwordMinLength')}</li>
                      <li>{t('register.passwordComplexity')}</li>
                      <li>{t('register.passwordNotCommon')}</li>
                      <li>{t('register.passwordLatinOnly')}</li>
                      <li>{t('register.passwordNoEmail')}</li>
                    </ul>
                  </div>
                </div>
                
                <div>
                  <label htmlFor="password-confirm-input" className="block text-sm font-bold text-neobrutalism-text mb-2">
                    {t('setNewPassword.confirmPasswordLabel')}
                  </label>
                  <input
                    id="password-confirm-input"
                    {...register('passwordConfirm', {
                      required: t('setNewPassword.confirmPasswordRequired'),
                      validate: value => {
                        const { password } = getValues();
                        return value === password || t('setNewPassword.passwordMismatch');
                      }
                    })}
                    type="password"
                    className="input-neobrutalism w-full"
                    placeholder={t('setNewPassword.confirmPasswordPlaceholder')}
                  />
                  {errors.passwordConfirm && (
                    <p className="mt-1 text-sm font-bold text-red-600">{errors.passwordConfirm.message}</p>
                  )}
                </div>
              </div>
              
              <button 
                type="submit" 
                className="btn-neobrutalism w-full text-xs sm:text-base py-2 sm:py-3 px-2 sm:px-4"
                disabled={isSubmitting}
              >
                {isSubmitting ? t('setNewPassword.submitting') : t('setNewPassword.submitButton')}
              </button>
            </form>
          </div>
        </div>
      </div>
    </>
  )
} 